import { isValidISO, getDiff } from '../util/date';
import { trim } from './trim';

function findNestedVal(nestedObj, list) {
  return nestedObj.reduce((total, val) => (total && total[val] ? total[val] : null), list);
}

/**
 * @description Sorts numbers from high -> low or low -> high but places nulls always last.
 * @param {*} a
 * @param {*} b
 * @param {*} sortBy
 * @param {boolean|undefined} asc
 */
function compareNumbers(a, b, sortBy, asc) {
  if (typeof sortBy === 'function') {
    return parseFloat(sortBy(a)) - parseFloat(sortBy(b));
  }

  const aValue = Array.isArray(sortBy) ? findNestedVal(sortBy, a) : a[sortBy];
  const bValue = Array.isArray(sortBy) ? findNestedVal(sortBy, b) : b[sortBy];

  /**
   * Places nulls after values
   */
  if (bValue === null) return -1;
  if (aValue === null) return 1;
  if (aValue === bValue) return 0;

  /**
   * Sorts values
   */
  if (asc) return parseFloat(aValue) < parseFloat(bValue) ? -1 : 1;
  return parseFloat(aValue) < parseFloat(bValue) ? 1 : -1;
}

/**
 * @description Takes a string, sanitizes it, returns it uppercased.
 * @param {string} str
 * @return {string}
 */
function normalizeString(str) {
  return encodeURI(trim(str.toString())).toUpperCase();
}

/**
 * @description Compares strings and has added functionality to choose sort direction.
 * @param {*} a
 * @param {*} b
 * @param {*} sortBy
 * @param {boolean|undefined} asc
 */
export function compareStrings(a, b, sortBy, asc) {
  if (typeof sortBy === 'function') {
    return sortBy(a).toUpperCase().localeCompare(sortBy(b).toUpperCase());
  }

  if (Array.isArray(a[sortBy])) {
    if (a[sortBy].length > 0) return -1;
    return 1;
  }

  const aValue = Array.isArray(sortBy) ? findNestedVal(sortBy, a) : a[sortBy];
  const bValue = Array.isArray(sortBy) ? findNestedVal(sortBy, b) : b[sortBy];

  if (!aValue) return 1;
  if (!bValue) return -1;

  if (isValidISO(aValue) && isValidISO(bValue)) {
    if (asc) return getDiff(aValue, bValue).ms;
    return getDiff(bValue, aValue).ms;
  }

  if (asc) return normalizeString(aValue).localeCompare(normalizeString(bValue));
  return normalizeString(bValue).localeCompare(normalizeString(aValue));
}

export function sortList(list, sortBy, isNumber, asc) {
  const sortCompare = isNumber ? compareNumbers : compareStrings;
  return list.sort((a, b) => sortCompare(a, b, sortBy, typeof asc === 'boolean' ? asc : true));
}

export const sortUnisexSizeList = list =>
  list.sort((a, b) => {
    const aSize = a.name?.split('M ')[1].split(' /')[0];
    const bSize = b.name?.split('M ')[1].split(' /')[0];
    return Number(aSize) - Number(bSize);
  });
